import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
    AlertTriangle, Archive, ArrowLeft, ArrowRight, BarChart3, Building2, Check, CheckCircle2, ChevronDown, ChevronRight,
    CircleHelp, Clock, Copy, Download, Edit, Gamepad2, Hourglass, Mail, MapPin, Monitor, MonitorOff, MonitorX,
    MoreHorizontal, Phone, Plus, Ticket, Trash2, User, X,
} from 'lucide-react';
import useAuthStore, { PERMISSIONS } from '../stores/authStore';
import api from '../api/client';
import { CAFE_STATUS_CONFIG } from '../components/CafeTable';
import CafeLifecycle, { ArchiveModal } from '../components/CafeLifecycle';
import { LIFECYCLE_BADGE } from '../components/cafeLifecycleBadge';
import CafeInsightsModal from '../components/CafeInsightsModal';
import { APPROX_HINT } from '../utils/registrationDate';

/**
 * A café's main page (/cafes/:id): who it is, how it is doing, and its PCs.
 *
 * Two views of the same PCs, picked by a switch (2026-10-16, for the two
 * people who read this page). It always opens on Overview — not remembered,
 * by request:
 *   Overview     management's: "N of M PCs working" with a bar, then only the
 *                PCs that need something — uninstalled, stuck on an old
 *                version, missing — each as one plain sentence, and the rest
 *                as small tiles.
 *   All details  the tech team's: every PC on one line with every column,
 *                problems first, the seven filter cards, sorting, CSV.
 * Clicking a PC in either opens its details panel: everything about it, plus
 * Remove PC and a way to its remote page. Selecting text in a row still just
 * selects it (rows used to be unclickable so text could be copied).
 *
 * Data: GET /internal/cafes/{id}/pc-details (the PC list, History, Contact)
 * and GET /internal/cafes/{id} (status, owner's cafés, revenue — what the
 * header buttons need). The live tiles and remote commands are on their own
 * page, /cafes/:id/remote (CafeRemote.jsx). The old /cafes/:id/details address
 * redirects here: Teams alert cards already sent carry it.
 *
 * Total PCs = PCs on in the last 90 days, the same count as the daily
 * utilization email and the rest of SuperAdmin; older PCs are kept, apart
 * (collapsed in Overview, greyed at the bottom of All details), and left out
 * of every count.
 *
 * Round 2 (2026-10-16): Overview's groups are boxes coloured by how serious —
 * Uninstalled red, Missing orange, Old version amber — picked with the chips
 * under the bar, which run in the bar's order (good to bad) while the boxes
 * run worst first. "Stuck" is never shown on its own: it read as a frozen PC. "Working normally" is only PCs on in the last WORKING_DAYS; the rest
 * wait under "Not seen for a while" until the sweep confirms them missing.
 * Console controllers (the PlayStation laptops) and Lost PCs (90+ days) have
 * grey groups of their own and are left out of every count.
 */

const IST = 'Asia/Kolkata';

// Backend sends offset-aware ISO; a bare one is UTC, like everywhere else here.
const toMs = (iso) => {
    if (!iso) return null;
    const t = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`).getTime();
    return Number.isNaN(t) ? null : t;
};

const dateText = (iso, approx = false) => {
    const t = toMs(iso);
    if (t == null) return null;
    const text = new Date(t).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: IST });
    return approx ? `~${text}` : text;
};

const exactText = (iso) => {
    const t = toMs(iso);
    if (t == null) return undefined;
    return new Date(t).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: IST,
    });
};

/** "just now", "12 min ago", "5h ago", "3d ago" — days however long ago, so
 * every time on the page reads the same way. */
const agoText = (iso) => {
    const t = toMs(iso);
    if (t == null) return null;
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 120) return 'just now';
    if (s < 3600) return `${Math.floor(s / 60)} min ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
};

const daysText = (iso) => {
    const t = toMs(iso);
    if (t == null) return 'a while';
    const d = Math.floor((Date.now() - t) / 86400000);
    return `${d} day${d === 1 ? '' : 's'}`;
};

// "15 Oct", the year only when it is not this one.
const shortDate = (iso) => {
    const t = toMs(iso);
    if (t == null) return null;
    const sameYear = new Date(t).getFullYear() === new Date().getFullYear();
    return new Date(t).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }), timeZone: IST,
    });
};

// "Working normally" on Overview = on within this many days.
const WORKING_DAYS = 7;
const seenWithinDays = (iso, days) => {
    const t = toMs(iso);
    return t != null && Date.now() - t <= days * 86400000;
};

// Same wording as the 8 AM utilization email (pc_activity_report.utilization_text).
const percentText = (used, total) => {
    if (!total) return '0%';
    const v = Math.min(used / total, 1);
    if (v <= 0) return '0%';
    if (v < 0.01) return '<1%';
    return `${Math.round(v * 100)}%`;
};

// Live state first (a PC on right now is not missing, whatever the last
// 30-minute sweep said), then the PC's history.
const STATUS = {
    missing: { label: 'Missing', badge: 'danger', rank: 0 },
    uninstalled: { label: 'Uninstalled', badge: 'danger', rank: 1 },
    offline: { label: 'Offline', badge: 'neutral', rank: 2 },
    idle: { label: 'Idle', badge: 'warning', rank: 3 },
    inuse: { label: 'In use', badge: 'success', rank: 4 },
    old: { label: 'Lost', badge: 'neutral', rank: 5 },
    console: { label: 'Console controller', badge: 'neutral', rank: 6 },
};

// Where a PC stands against the kiosk version its café is told to run
// (backend app/tasks/kiosk_version_alerts.py). No version_state = nothing to
// compare with: no recent check, nothing pinned, or an old PC.
const VERSION_STATE = {
    up_to_date: { label: 'Up to date' },
    stuck: { label: 'Old version' },
    waiting: { label: 'Updates when switched on' },
    unknown: { label: 'Not reported' },
};

const statusOf = (pc) => {
    if (pc.console) return 'console';
    if (pc.online) return pc.current_user_id ? 'inuse' : 'idle';
    if (pc.old) return 'old';
    if (pc.uninstalled_at) return 'uninstalled';
    if (pc.missing) return 'missing';
    return 'offline';
};

const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true, sensitivity: 'base' });

const compareVersions = (a, b) => {
    const pa = String(a).split(/[.-]/);
    const pb = String(b).split(/[.-]/);
    for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
        const x = pa[i] ?? '';
        const y = pb[i] ?? '';
        const d = /^\d+$/.test(x) && /^\d+$/.test(y) ? Number(x) - Number(y) : x.localeCompare(y);
        if (d) return d;
    }
    return 0;
};

// `first` = the direction of the first click: whatever needs a look goes on top.
const COLUMNS = [
    { key: 'name', label: 'PC', first: 'asc', value: (p) => p.name },
    { key: 'status', label: 'Status', first: 'asc', value: (p) => STATUS[p.state].rank },
    { key: 'seen', label: 'Last seen', first: 'asc', value: (p) => toMs(p.last_seen) },
    { key: 'used', label: 'Last used', first: 'asc', value: (p) => toMs(p.last_used) },
    { key: 'version', label: 'Version', first: 'asc', value: (p) => p.client_version, compare: compareVersions },
    { key: 'installed', label: 'Installed', first: 'desc', value: (p) => toMs(p.registered_at) },
    { key: 'reinstall', label: 'Last reinstall', first: 'desc', value: (p) => toMs(p.last_reinstalled_at) },
    { key: 'uninstalled', label: 'Uninstalled', first: 'desc', value: (p) => toMs(p.uninstalled_at) },
];

// What a PC needs, worst first: 0 uninstalled, 1 stuck on an old version,
// 2 missing, 3 nothing. A stuck PC that has also gone missing is missing —
// it is not being used, so its version is not what to chase.
const problemOf = (pc) => {
    if (pc.old || pc.console) return null;
    if (pc.state === 'uninstalled') return 'uninstalled';
    if (pc.state === 'missing') return 'missing';
    if (pc.version_state === 'stuck') return 'stuck';
    return null;
};
const PROBLEM_RANK = { uninstalled: 0, stuck: 1, missing: 2 };
// Kept below everything else in All details, and out of every count.
const isApart = (pc) => pc.old || pc.console;
// Reinstalled this often, a problem row says so: repeated reinstalls are a
// sign of trouble on that machine.
const REINSTALL_TAG_FROM = 2;

// The default order of All details and of the panel's ↑ / ↓: problems first
// (newest silence first among the missing), then everything else by name, old
// PCs last.
const ATTENTION = { key: 'attention' };
const compareAttention = (a, b) => {
    if (isApart(a) !== isApart(b)) return isApart(a) ? 1 : -1;
    const pa = PROBLEM_RANK[problemOf(a)] ?? 9;
    const pb = PROBLEM_RANK[problemOf(b)] ?? 9;
    if (pa !== pb) return pa - pb;
    if (problemOf(a) === 'missing') return (toMs(b.last_seen) || 0) - (toMs(a.last_seen) || 0);
    if (problemOf(a) === 'uninstalled') return (toMs(b.uninstalled_at) || 0) - (toMs(a.uninstalled_at) || 0);
    return byName(a, b);
};

// Old PCs always last, blanks always last within each group, then by name.
const compareRows = (col, dir) => (a, b) => {
    if (isApart(a) !== isApart(b)) return isApart(a) ? 1 : -1;
    const va = col.value(a);
    const vb = col.value(b);
    const blankA = va == null || va === '';
    const blankB = vb == null || vb === '';
    if (blankA || blankB) return blankA === blankB ? byName(a, b) : (blankA ? 1 : -1);
    let d;
    if (col.compare) d = col.compare(va, vb);
    else if (typeof va === 'number') d = va - vb;
    else d = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    if (d) return dir === 'asc' ? d : -d;
    return byName(a, b);
};

// "2026-10-07 14:15" in IST — sorts correctly in a spreadsheet.
const csvTime = (iso) => {
    const t = toMs(iso);
    if (t == null) return '';
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: IST,
    }).formatToParts(new Date(t)).map((x) => [x.type, x.value]));
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
};

// Quoted, and a leading = + - @ neutralised so a PC name can never run as a
// spreadsheet formula.
const csvCell = (v) => {
    let s = v == null ? '' : String(v);
    if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
    return `"${s.replace(/"/g, '""')}"`;
};

// Billing & Revenue's "Billing from" wording and rule, unchanged: no date is a
// free trial, a date that has not taken effect is a trial until then, and
// fee_active (the café's date AND the fleet-wide switch) is paying.
const istDay = (iso) => {
    const t = toMs(iso);
    if (t == null) return null;
    return new Intl.DateTimeFormat('en-IN', { timeZone: IST, day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(t));
};
// A trial ending within TRIAL_SOON_DAYS says so in words and gets an outline,
// so it is not missed among the other badges.
const TRIAL_SOON_DAYS = 7;
const feeBadge = (terms) => {
    if (!terms) return null;
    const startsOn = istDay(terms.fee_starts_at);
    if (!startsOn) return { label: 'Free trial', color: 'neutral' };
    if (terms.fee_active) return { label: `Paying since ${startsOn}`, color: 'success' };
    const days = Math.ceil((toMs(terms.fee_starts_at) - Date.now()) / 86400000);
    if (days >= 0 && days <= TRIAL_SOON_DAYS) {
        const when = days === 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`;
        return { label: `Trial ends ${when} · ${startsOn}`, color: 'warning', soon: true };
    }
    return { label: `Trial until ${startsOn}`, color: 'warning' };
};

const formatCurrency = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const slug = (s) => String(s || 'cafe').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'cafe';

/** The rows on screen — the current card's filter and sort — as a CSV download. */
function exportCsv(cafeName, filterKey, rows) {
    const head = [
        'PC', 'Status', 'Installed', 'Install date is a guess', 'Last reinstall', 'Times reinstalled',
        'Uninstalled', 'Last seen', 'Last used', 'Version', 'Should be on', 'Version status', 'Used in 24h', 'Used in 72h',
    ];
    const lines = rows.map((pc) => [
        pc.name,
        STATUS[pc.state].label,
        csvTime(pc.registered_at),
        pc.registered_at && pc.registered_at_approx ? 'Yes' : '',
        csvTime(pc.last_reinstalled_at),
        pc.reinstall_count || 0,
        csvTime(pc.uninstalled_at),
        csvTime(pc.last_seen),
        csvTime(pc.last_used),
        pc.client_version || '',
        pc.expected_version || '',
        VERSION_STATE[pc.version_state]?.label || '',
        pc.used_24h ? 'Yes' : 'No',
        pc.used_72h ? 'Yes' : 'No',
    ]);
    const csv = [head, ...lines].map((r) => r.map(csvCell).join(',')).join('\r\n');
    // BOM so Excel reads the café's name (é) correctly.
    const url = URL.createObjectURL(new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    const which = filterKey === 'all' ? '' : `-${filterKey}`;
    a.href = url;
    a.download = `${slug(cafeName)}-pcs${which}-${csvTime(new Date().toISOString()).slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

const CARDS = [
    { key: 'all', label: 'Total PCs', test: () => true },
    { key: 'inuse', label: 'In use now', test: (p) => p.state === 'inuse' },
    { key: 'used24', label: 'Used in 24h', test: (p) => p.used_24h, percent: true },
    { key: 'used72', label: 'Used in 72h', test: (p) => p.used_72h },
    { key: 'missing', label: 'Missing', test: (p) => p.state === 'missing', alarm: true },
    { key: 'uninstalled', label: 'Uninstalled', test: (p) => p.state === 'uninstalled', alarm: true },
    // Amber, like its Teams card: check, rather than act now.
    { key: 'stuck', label: 'Old version', test: (p) => p.version_state === 'stuck', warn: true },
];

/** A date with a small "approx." tag when it is a best guess (see APPROX_HINT). */
function ApproxDate({ iso, approx }) {
    const text = dateText(iso);
    if (!text) return <span className="cafe-pcs__blank">Not recorded</span>;
    return (
        <>
            {text}
            {approx && <span className="cafe-pcs__approx" title={APPROX_HINT}>approx.</span>}
        </>
    );
}

const copyViaTextarea = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
        return document.execCommand('copy');
    } catch {
        return false;
    } finally {
        ta.remove();
    }
};

// The clipboard API first; the older textarea route when the page is not
// secure or the browser refuses the API (some embedded browsers do).
const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text);
            return;
        } catch {
            // fall through to the textarea route
        }
    }
    if (!copyViaTextarea(text)) throw new Error('copy failed');
};

/** Copies `text`, then says so for a moment. */
function CopyButton({ text, label, children }) {
    const [state, setState] = useState(null); // null | 'ok' | 'fail'
    useEffect(() => {
        if (!state) return undefined;
        const timer = setTimeout(() => setState(null), 1500);
        return () => clearTimeout(timer);
    }, [state]);
    return (
        <button
            type="button"
            className={`cafe-pcs__copy${state === 'ok' ? ' cafe-pcs__copy--ok' : ''}`}
            title={label}
            aria-label={label}
            onClick={() => copyText(text).then(() => setState('ok'), () => setState('fail'))}
        >
            {state === 'ok' && <><Check size={14} /> Copied</>}
            {state === 'fail' && 'Could not copy'}
            {!state && <><Copy size={14} />{children}</>}
        </button>
    );
}

// Whether History + Contact were left open, kept in the person's browser.
// Best effort: unreadable storage just means closed.
const INFO_OPEN_KEY = 'nolag.superadmin.cafePage.infoOpen';
// History and Contact used to open one by one; someone who kept either open
// starts with the merged section open.
const LEGACY_OPEN_BOXES_KEY = 'nolag.superadmin.cafePage.openBoxes';
function readStored(key) {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}
function writeStored(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Storage blocked (private window etc.): it just won't be remembered.
    }
}
function readInfoOpen() {
    const saved = readStored(INFO_OPEN_KEY);
    if (saved != null) return saved === '1';
    try {
        const legacy = JSON.parse(readStored(LEGACY_OPEN_BOXES_KEY) || '{}');
        return legacy.history === true || legacy.contact === true;
    } catch {
        return false;
    }
}

// The coloured dot beside a time: green today or yesterday, amber within a
// week, red past it.
const ageDot = (iso) => {
    const t = toMs(iso);
    if (t == null) return 'r';
    const days = (Date.now() - t) / 86400000;
    if (days < 2) return 'g';
    return days <= 7 ? 'a' : 'r';
};

/** The version, and where it stands, on one line. */
function VersionLine({ pc }) {
    const v = pc.client_version;
    if (pc.version_state === 'stuck') {
        return <span className="cafe-pcs__v-stuck">{v} → {pc.expected_version}</span>;
    }
    if (pc.version_state === 'up_to_date') return <span>{v} <span className="cafe-pcs__v-ok">✓</span></span>;
    if (pc.version_state === 'waiting') return <span>{v} <span className="cafe-pcs__muted">· updates when on</span></span>;
    if (pc.version_state === 'unknown') return <span className="cafe-pcs__muted">Not reported</span>;
    return v ? <span>{v}</span> : <span className="cafe-pcs__blank">—</span>;
}

// True on a screen too narrow for the panel beside the list (the CSS's 900px).
const NARROW_QUERY = '(max-width: 900px)';
function useNarrow() {
    const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);
    useEffect(() => {
        const mq = window.matchMedia(NARROW_QUERY);
        const onChange = () => setNarrow(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return narrow;
}

// Closes a popup on a click outside `ref` or on Escape.
function useDismiss(open, setOpen, ref) {
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, setOpen, ref]);
}

/** The header's ⋯ button: the actions used now and then, not every visit. */
function MoreMenu({ items }) {
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);
    useDismiss(open, setOpen, boxRef);
    if (items.length === 0) return null;
    return (
        <span className="cafe-pcs__more" ref={boxRef}>
            <button
                type="button"
                className="btn btn--secondary cafe-pcs__more-btn"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="More actions"
                title="More actions"
                onClick={() => setOpen((v) => !v)}
            >
                <MoreHorizontal size={18} />
            </button>
            {open && (
                <span className="cafe-pcs__menu" role="menu">
                    {items.map((item) => (
                        <button
                            key={item.label}
                            type="button"
                            role="menuitem"
                            className={`cafe-pcs__menu-item${item.danger ? ' is-danger' : ''}`}
                            onClick={() => { setOpen(false); item.onClick(); }}
                        >
                            <item.icon size={15} /> {item.label}
                        </button>
                    ))}
                </span>
            )}
        </span>
    );
}

// The one sentence a problem row says.
const problemSentence = (pc) => {
    const p = problemOf(pc);
    if (p === 'uninstalled') return <>Uninstalled <b>{shortDate(pc.uninstalled_at)}</b> · not set up again</>;
    if (p === 'stuck') {
        return <>On <span className="cafe-pcs__hl">{pc.client_version}</span>, should be {pc.expected_version}</>;
    }
    if (p === 'missing') return <>Not connected for <b>{daysText(pc.last_seen)}</b></>;
    return null;
};

// Overview's groups (2026-10-16 round 2), most serious first. Uninstalled is
// red — certain and deliberate; Missing orange — it may just be switched off;
// Old version amber — it works, just behind. The grey ones are not counted.
const SECTIONS = {
    uninstalled: {
        title: 'Uninstalled', chip: 'Uninstalled', icon: MonitorX, color: 'var(--pc-uninstalled)',
        help: 'NOLAG was removed from these PCs and they have not been set up again.',
    },
    stuck: {
        title: 'On an old version', chip: 'Old version', icon: Hourglass, color: 'var(--pc-stuck)',
        help: 'Used on 2 or more days since the new version came out, and still not updated.',
    },
    missing: {
        title: 'Missing', chip: 'Missing', icon: MonitorOff, color: 'var(--pc-missing)',
        help: 'Not connecting while other PCs at the café are in use (6 checks in a row).',
    },
    fine: {
        title: 'Working normally', chip: 'Working normally', icon: CheckCircle2, color: 'var(--status-success)',
        help: `Connected in the last ${WORKING_DAYS} days with nothing wrong. PCs on an old version work too; they have their own box.`,
    },
    quiet: {
        title: 'Not seen lately', chip: 'Not seen lately', icon: Clock, color: 'var(--pc-grey)',
        help: `Off for more than ${WORKING_DAYS} days, but not confirmed missing: that needs other PCs here to be in use meanwhile.`,
    },
    console: {
        title: 'Console controllers', chip: 'Console controllers', icon: Gamepad2, color: 'var(--pc-grey)',
        help: 'Laptops that control the PlayStations. Not counted as PCs and never alerted on.',
    },
    lost: {
        title: 'Lost — not seen in 90+ days', chip: 'Lost', icon: Archive, color: 'var(--pc-grey)',
        help: 'Probably replaced or gone for good. Left out of every count; remove them to tidy up.',
    },
};
// On screen in this order; the bar's segments in BAR_ORDER.
const SECTION_ORDER = ['uninstalled', 'stuck', 'missing', 'fine', 'quiet', 'console', 'lost'];
const BAR_ORDER = ['fine', 'stuck', 'quiet', 'missing', 'uninstalled'];
// The bar's hover card: what each piece means, in a few words.
const BAR_TIP = {
    fine: `On in the last ${WORKING_DAYS} days, nothing wrong`,
    stuck: 'Working, but not updated to the new version yet',
    quiet: `Off for over ${WORKING_DAYS} days, not confirmed missing yet`,
    missing: 'Not connecting while other PCs here are in use',
    uninstalled: 'NOLAG removed and not set up again',
};
// Half the hover card's width (the CSS's 270px): keeps it inside the box.
const BAR_TIP_HALF = 135;
const TILE_SECTIONS = new Set(['fine', 'console']);
const GREY_SECTIONS = new Set(['quiet', 'console', 'lost']);
// A long list of rows shows this many, then "Show all N".
const SECTION_CAP = 5;

// Which group a PC is in on Overview. A problem wins over "not seen for a
// while": a missing PC has been off for a while by definition.
const kindOf = (pc) => {
    if (pc.console) return 'console';
    if (pc.old) return 'lost';
    const problem = problemOf(pc);
    if (problem) return problem;
    if (!pc.online && !seenWithinDays(pc.last_seen, WORKING_DAYS)) return 'quiet';
    return 'fine';
};

const adviceOf = (pc, kind) => ({
    uninstalled: `NOLAG was uninstalled on ${dateText(pc.uninstalled_at)} and not set up again. If the café still uses this PC, install NOLAG again.`,
    stuck: `Used on 2 or more days since the update, still on ${pc.client_version}. Check its auto-update from the Admin Console.`,
    missing: `Not connected for ${daysText(pc.last_seen)} while other PCs here are in use. Check with the café: it may be off, unplugged or have had NOLAG removed.`,
    quiet: `Off for ${daysText(pc.last_seen)}. It shows as Missing if it stays off while other PCs here are in use.`,
    lost: 'Not seen in over 90 days, so it is left out of every count. Remove it if the café no longer has it.',
    console: 'Controls the PlayStations. Not counted as a PC and never alerted on.',
}[kind] || null);

/** The panel's version line: what it runs and where that stands. */
function PanelVersion({ pc }) {
    const v = pc.client_version;
    if (!v) return <span className="cafe-pcs__blank">Not reported</span>;
    if (pc.version_state === 'stuck') {
        return (
            <>
                <span className="cafe-pcs__v-stuck">{v}</span> → should be {pc.expected_version}
                {pc.version_since && <span className="cafe-pcs__muted"> · since {shortDate(pc.version_since)}</span>}
            </>
        );
    }
    if (pc.version_state === 'up_to_date') return <>{v} <span className="cafe-pcs__v-ok">✓ up to date</span></>;
    if (pc.version_state === 'waiting') return <>{v} <span className="cafe-pcs__muted">· updates to {pc.expected_version} when on</span></>;
    return <>{v}</>;
}

/** Everything about one PC, beside the list — short enough to fit a laptop
 * screen without a scrollbar of its own. */
function PcPanel({ pc, cafeId, canEdit, onClose, onRemoved, floating }) {
    const navigate = useNavigate();
    const [techOpen, setTechOpen] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [removeError, setRemoveError] = useState(null);
    const kind = kindOf(pc);
    const advice = adviceOf(pc, kind);
    const st = STATUS[pc.state];
    const n = pc.reinstall_count || 0;

    // Same removal and confirm wording as the remote page's PC panel.
    const removePc = async () => {
        if (!window.confirm(`Remove ${pc.name} from this café's registry? This cannot be undone.`)) return;
        setRemoving(true);
        setRemoveError(null);
        try {
            await api.delete(`/internal/pcs/${cafeId}/${pc.id}`);
            onRemoved();
        } catch (err) {
            setRemoveError(err.response?.data?.detail || 'Could not remove this PC.');
            setRemoving(false);
        }
    };

    const panel = (
        <aside className={`cafe-pcs__panel${floating ? ' is-floating' : ''}`} aria-label={`${pc.name} details`}>
            <div className="cafe-pcs__panel-head">
                <div className="cafe-pcs__panel-title">
                    <h2>{pc.name}</h2>
                    {kind === 'fine'
                        ? <span className={`badge badge--${st.badge}`}>{st.label}</span>
                        : <span className="cafe-pcs__pill" style={{ '--c': SECTIONS[kind].color }}>{{ console: 'Console controller', lost: 'Lost' }[kind] || SECTIONS[kind].title}</span>}
                </div>
                <button type="button" className="cafe-pcs__panel-close" onClick={onClose} aria-label="Close" title="Close (Esc)">
                    <X size={18} />
                </button>
            </div>
            <div className="cafe-pcs__panel-actions">
                <button type="button" className="btn btn--primary btn--sm" onClick={() => navigate(`/cafes/${cafeId}/remote`)}>
                    <Monitor size={14} /> Open remote page
                </button>
                {canEdit && (
                    <button type="button" className="btn btn--secondary btn--sm cafe-pcs__danger-btn" onClick={removePc} disabled={removing}>
                        <Trash2 size={14} /> {removing ? 'Removing…' : 'Remove'}
                    </button>
                )}
            </div>
            {removeError && <p className="modal-error">{removeError}</p>}
            {advice && <p className="cafe-pcs__advice" style={{ '--c': SECTIONS[kind].color }}>{advice}</p>}

            <dl className="cafe-pcs__kv">
                <dt>Version</dt><dd><PanelVersion pc={pc} /></dd>
                <dt>Last seen</dt>
                <dd title={exactText(pc.last_seen)}>{pc.online ? 'Now' : (agoText(pc.last_seen) || <span className="cafe-pcs__blank">Never</span>)}</dd>
                {!pc.console && (
                    <>
                        <dt>Last used</dt>
                        <dd title={exactText(pc.last_used)}>
                            {pc.state === 'inuse' ? 'Now' : (agoText(pc.last_used) || <span className="cafe-pcs__blank">Not in 90 days</span>)}
                        </dd>
                        <dt>Used in 24h / 72h</dt><dd>{pc.used_24h ? 'Yes' : 'No'} / {pc.used_72h ? 'Yes' : 'No'}</dd>
                    </>
                )}
                <dt>Installed</dt><dd><ApproxDate iso={pc.registered_at} approx={pc.registered_at_approx} /></dd>
                {/* History only when there is some. */}
                {pc.last_reinstalled_at && (
                    <><dt>Reinstalled</dt><dd>{dateText(pc.last_reinstalled_at)} <span className="cafe-pcs__muted">({n} time{n === 1 ? '' : 's'})</span></dd></>
                )}
                {pc.uninstalled_at && (
                    <><dt>Uninstalled</dt><dd className="cafe-pcs__alarm">{dateText(pc.uninstalled_at)}</dd></>
                )}
            </dl>

            <button type="button" className="cafe-pcs__tech-toggle" aria-expanded={techOpen} onClick={() => setTechOpen((v) => !v)}>
                <ChevronRight size={14} className={techOpen ? 'is-open' : undefined} /> Technical details
                {!techOpen && <span className="cafe-pcs__muted"> (MAC, IP, PC number)</span>}
            </button>
            {techOpen && (
                <dl className="cafe-pcs__kv">
                    <dt>MAC address</dt>
                    <dd>{pc.mac_address ? <>{pc.mac_address} <CopyButton text={pc.mac_address} label="Copy MAC address" /></> : <span className="cafe-pcs__blank">Not reported by this version</span>}</dd>
                    <dt>IP address</dt>
                    <dd>{pc.ip_address ? <>{pc.ip_address} <CopyButton text={pc.ip_address} label="Copy IP address" /></> : <span className="cafe-pcs__blank">Not reported</span>}</dd>
                    <dt>PC number</dt><dd>#{pc.id}</dd>
                </dl>
            )}
        </aside>
    );
    // Floating (over the table, or full screen on a phone) it is portalled to
    // <body>: inside the page it sat under the app header's own layer.
    return floating ? createPortal(panel, document.body) : panel;
}

/** "Owner's cafés (4) ▾" beside the café name: the owner's cafés, this one ticked. */
function OwnerCafesPicker({ cafes, currentId, onPick }) {
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);
    return (
        <span className="owner-picker" ref={boxRef}>
            <button
                type="button"
                className="owner-picker__btn"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                Owner&apos;s cafés ({cafes.length}) <ChevronDown size={14} />
            </button>
            {open && (
                <span className="owner-picker__menu" role="menu">
                    {cafes.map((c) => {
                        const current = c.id === currentId;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                role="menuitem"
                                className={`owner-picker__item${current ? ' is-current' : ''}`}
                                onClick={() => { setOpen(false); if (!current) onPick(c.id); }}
                            >
                                <span>{c.name}{c.location ? ` — ${c.location}` : ''}</span>
                                {current && <Check size={14} />}
                            </button>
                        );
                    })}
                </span>
            )}
        </span>
    );
}

// A fresh page per café: the owner's-cafés switcher changes :id in place, and
// keying on it means nothing from the café just left (its data, a slow reply,
// the chosen filter) can carry over to the one now shown.
export default function CafePcDetailsPage() {
    const { id } = useParams();
    return <CafePcDetails key={id} />;
}

function CafePcDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const highlight = (searchParams.get('pc') || '').trim().toLowerCase();
    // ?pcid= (a support ticket's PC chip) finds the PC by its id, so it still
    // lands on the right row after the PC is renamed.
    const highlightId = searchParams.get('pcid') || '';

    const { hasPermission, isSuperAdmin } = useAuthStore();
    const canEdit = hasPermission(PERMISSIONS.EDIT_CAFE_DETAILS) || isSuperAdmin();

    const [data, setData] = useState(null);
    // GET /internal/cafes/{id}: status, owner's cafés, revenue, the owner
    // fields "Add café for this owner" prefills. Loaded alongside `data`; either
    // one is enough to show the page — a deleted café has no PCs to read but
    // must still open, with its lifecycle panel.
    const [cafe, setCafe] = useState(null);
    const [feeTerms, setFeeTerms] = useState(null);
    const [tickets, setTickets] = useState(null);
    const [showStats, setShowStats] = useState(false);
    // History + Contact: one summary line, "More" opens both in full. Each
    // person's choice is kept in their browser.
    const [infoOpen, setInfoOpen] = useState(readInfoOpen);
    const toggleInfo = () => {
        writeStored(INFO_OPEN_KEY, infoOpen ? '0' : '1');
        setInfoOpen(!infoOpen);
    };
    // Always opens on Overview (user, 2026-10-16: not remembered).
    const [view, setView] = useState('overview');
    // undefined = nothing chosen yet, so a ?pc= / ?pcid= link's PC opens;
    // null = closed.
    const [selectedId, setSelectedId] = useState(undefined);
    const [lostOpen, setLostOpen] = useState(false);
    // The bar piece being hovered or focused: { key, left, arrow } in px.
    const [barTip, setBarTip] = useState(null);
    const barRef = useRef(null);
    // Overview's chip: 'all' or one group (SECTIONS). Not remembered.
    const [group, setGroup] = useState('all');
    const [archiving, setArchiving] = useState(false);
    const narrow = useNarrow();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [sort, setSort] = useState(ATTENTION);
    const highlightRef = useRef(null);

    const loadCafe = useCallback(() => api.get(`/internal/cafes/${id}`).then((res) => setCafe(res.data)), [id]);
    const load = useCallback(() => Promise.allSettled([
            api.get(`/internal/cafes/${id}/pc-details`).then((res) => setData(res.data)),
            loadCafe(),
        ]).then(([pcRes, cafeRes]) => {
            if (pcRes.status === 'rejected') {
                setError(pcRes.reason?.response?.data?.detail || 'Could not load this café.');
            }
            if (cafeRes.status === 'rejected') console.error('Failed to load café:', cafeRes.reason);
        }), [id, loadCafe]);

    // One instance per café (see the default export), so this runs once.
    useEffect(() => {
        let cancelled = false;
        load().finally(() => { if (!cancelled) setIsLoading(false); });
        // The two header badges: optional, so a failure just leaves one out.
        api.get(`/internal/cafes/${id}/fee-terms`)
            .then((res) => { if (!cancelled) setFeeTerms(res.data); })
            .catch((err) => console.error('Failed to load fee terms:', err));
        api.get('/internal/support-tickets')
            .then((res) => {
                if (!cancelled) setTickets((res.data || []).filter((t) => t.cafe_id === Number(id) && t.status !== 'closed'));
            })
            .catch((err) => console.error('Failed to load support tickets:', err));
        return () => { cancelled = true; };
    }, [id, load]);

    const reload = () => {
        setIsLoading(true);
        setError(null);
        load().finally(() => setIsLoading(false));
    };

    const pcs = useMemo(() => (data?.pcs || []).map((pc) => ({ ...pc, state: statusOf(pc) })), [data]);
    // Every count is of these: on in the last 90 days, console controllers left out.
    const current = useMemo(() => pcs.filter((pc) => !pc.old && !pc.console), [pcs]);

    const counts = useMemo(
        () => Object.fromEntries(CARDS.map((c) => [c.key, current.filter(c.test).length])),
        [current],
    );

    const rows = useMemo(() => {
        const card = CARDS.find((c) => c.key === filter) || CARDS[0];
        const list = filter === 'all' ? pcs : current.filter(card.test);
        if (sort.key === ATTENTION.key) return [...list].sort(compareAttention);
        const col = COLUMNS.find((c) => c.key === sort.key) || COLUMNS[0];
        return [...list].sort(compareRows(col, sort.dir));
    }, [pcs, current, filter, sort]);

    // Overview's groups (SECTIONS). "Working normally" = nothing wrong and on
    // in the last WORKING_DAYS; a stuck PC is working too — customers use it.
    const groups = useMemo(() => {
        const g = Object.fromEntries(SECTION_ORDER.map((k) => [k, []]));
        for (const pc of pcs) g[kindOf(pc)].push(pc);
        // In use first, then the most recently seen.
        const recent = (a, b) => {
            if ((a.state === 'inuse') !== (b.state === 'inuse')) return a.state === 'inuse' ? -1 : 1;
            return (toMs(b.last_seen) || 0) - (toMs(a.last_seen) || 0) || byName(a, b);
        };
        for (const k of SECTION_ORDER) g[k].sort(PROBLEM_RANK[k] != null ? compareAttention : recent);
        return g;
    }, [pcs]);

    // A ?pc= / ?pcid= link (search, a ticket's PC chip, an alert) opens that
    // PC's panel until something else is picked.
    const isLinked = (pc) => (highlightId && String(pc.id) === highlightId)
        || (highlight && String(pc.name || '').toLowerCase() === highlight);
    const linkedPc = highlight || highlightId ? pcs.find(isLinked) : null;
    const openId = selectedId === undefined ? (linkedPc?.id ?? null) : selectedId;
    const selected = openId != null ? pcs.find((pc) => pc.id === openId) || null : null;

    // What each section shows right now: one group when a chip is picked,
    // else all of them with long lists cut to SECTION_CAP (never hiding the
    // PC that is open or linked to) and Lost folded.
    const shownKeys = group === 'all' ? SECTION_ORDER : [group];
    const focus = (pc) => pc.id === openId || isLinked(pc);
    const shown = Object.fromEntries(SECTION_ORDER.map((k) => {
        const list = groups[k];
        if (group !== 'all') return [k, list];
        if (k === 'lost') return [k, lostOpen || list.some(focus) ? list : []];
        if (!TILE_SECTIONS.has(k) && list.length > SECTION_CAP && !list.some(focus)) return [k, list.slice(0, SECTION_CAP)];
        return [k, list];
    }));

    // What ↑ / ↓ walks through while a panel is open: the order on screen.
    const order = view === 'details' ? rows : shownKeys.flatMap((k) => shown[k]);

    // A row click opens the panel — unless text was being selected in it.
    const openPc = (pc) => {
        if (window.getSelection && String(window.getSelection())) return;
        setSelectedId(pc.id);
    };
    const rowProps = (pc) => ({
        role: 'button',
        tabIndex: 0,
        onClick: () => openPc(pc),
        onKeyDown: (e) => {
            if (e.target !== e.currentTarget) return; // a button inside the row
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(pc.id); }
        },
        'aria-pressed': pc.id === openId,
    });

    useEffect(() => {
        if (openId == null) return undefined;
        const onKey = (e) => {
            if (e.target.closest?.('input, textarea, select, [role="dialog"]')) return;
            if (e.key === 'Escape') setSelectedId(null);
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
            const i = order.findIndex((pc) => pc.id === openId);
            const next = order[i + (e.key === 'ArrowDown' ? 1 : -1)];
            if (next) {
                e.preventDefault();
                setSelectedId(next.id);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [openId, order]);

    // First and latest install, over every PC the café has had.
    const installs = useMemo(() => {
        const dated = pcs.filter((p) => toMs(p.registered_at) != null)
            .sort((a, b) => toMs(a.registered_at) - toMs(b.registered_at));
        return dated.length ? { first: dated[0], latest: dated[dated.length - 1] } : null;
    }, [pcs]);

    useEffect(() => {
        if ((highlight || highlightId) && highlightRef.current) highlightRef.current.scrollIntoView({ block: 'center' });
    }, [highlight, highlightId, rows, view]);

    const onSort = (col) => setSort((s) => (s.key === col.key
        ? { key: col.key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key: col.key, dir: col.first }));

    if (isLoading && !data && !cafe) return <div className="loading-state"><div className="spinner" /></div>;

    if (!data && !cafe) return (
        <div className="empty-state">
            <Building2 size={48} />
            <p>{error || 'Could not load this café.'}</p>
            <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn--secondary" onClick={reload}>Try again</button>
                <button type="button" className="btn btn--ghost" onClick={() => navigate('/cafes')}>Back to Cafés</button>
            </div>
        </div>
    );

    // The PC list's café fields, or the café record's when the PCs could not
    // be read (a deleted café has no database left to read them from).
    const info = data || {
        name: cafe.name,
        owner: cafe.owner_name,
        phone: cafe.owner_phone,
        email: cafe.owner_email,
        location: cafe.location,
        created_at: cafe.created_at,
        created_at_approx: cafe.created_at_approx,
    };
    // Archived / deleting / deleted wins; an active café shows its licence.
    const status = LIFECYCLE_BADGE[cafe?.status]
        || (data ? CAFE_STATUS_CONFIG[data.license_status] || CAFE_STATUS_CONFIG.none : null);
    const isClosed = !!cafe?.status && cafe.status !== 'active';
    const fee = feeBadge(feeTerms);
    // A deleted café has no database left for the Statistics pop-up to read.
    const canStats = !!cafe && cafe.status !== 'purging' && cafe.status !== 'purged';
    const ownerCafes = cafe?.owner_cafes || [];
    const activeCard = CARDS.find((c) => c.key === filter) || CARDS[0];
    // One block to paste into WhatsApp or a ticket.
    const contactAll = [info.name, info.owner, info.phone, info.email, info.location].filter(Boolean).join('\n');
    // The one-line versions shown while a card is collapsed. Location is not
    // repeated in Contact's: it is always on show under the name.
    const historySummary = [
        dateText(info.created_at) && `Joined ${dateText(info.created_at)}`,
        cafe && `This month ${formatCurrency(cafe.stats?.revenue_mtd)}`,
        cafe && `All-time ${formatCurrency(cafe.stats?.revenue_all_time)}`,
    ].filter(Boolean).join(' · ');

    // The health box. A stuck PC counts as working — customers use it.
    const health = {
        total: current.length,
        working: groups.fine.length + groups.stuck.length,
    };
    // The chips under the bar: each shows only its group; All PCs (or the
    // picked chip again) shows everything. The grey ones, on the right, only
    // at a café that has any.
    const chips = [
        { key: 'all', label: 'All PCs', icon: Monitor, color: 'var(--accent-primary)', n: current.length },
        // Same order as the bar, so each chip sits under its own colour's turn.
        ...BAR_ORDER.map((k) => ({
            key: k, label: SECTIONS[k].chip, icon: SECTIONS[k].icon, color: SECTIONS[k].color, n: groups[k].length,
            flag: k === 'stuck' || k === 'missing' || k === 'uninstalled',
        })),
    ];
    const greyChips = ['console', 'lost']
        .filter((k) => groups[k].length > 0)
        .map((k) => ({ key: k, label: SECTIONS[k].chip, icon: SECTIONS[k].icon, color: SECTIONS[k].color, n: groups[k].length, small: true }));
    // Places the hover card under the piece, kept inside the box.
    const showBarTip = (key, el) => {
        const width = barRef.current?.clientWidth || 0;
        const centre = el.offsetLeft + el.offsetWidth / 2;
        const left = Math.min(Math.max(centre, BAR_TIP_HALF), Math.max(width - BAR_TIP_HALF, BAR_TIP_HALF));
        setBarTip({ key, left, arrow: centre });
    };
    const pickGroup = (key) => {
        setView('overview');
        setGroup(key === 'all' || (view === 'overview' && group === key) ? 'all' : key);
    };
    const renderChip = (c) => {
        const on = view === 'overview' && group === c.key;
        const Icon = c.icon;
        return (
            <button
                key={c.key}
                type="button"
                className={`cafe-pcs__chip${on ? ' is-on' : ''}${c.flag && c.n > 0 ? ' is-flag' : ''}${c.small ? ' is-small' : ''}`}
                style={{ '--c': c.color }}
                aria-pressed={on}
                disabled={c.n === 0 && c.key !== 'all'}
                onClick={() => pickGroup(c.key)}
            >
                <Icon size={c.small ? 14 : 16} /><b>{c.n}</b>{c.label}
            </button>
        );
    };

    // Lost rows have their own Remove, for tidying up several at once.
    const removeListed = async (pc) => {
        if (!window.confirm(`Remove ${pc.name} from this café's registry? This cannot be undone.`)) return;
        try {
            await api.delete(`/internal/pcs/${id}/${pc.id}`);
            if (pc.id === openId) setSelectedId(null);
            reload();
        } catch (err) {
            setError(err.response?.data?.detail || `Could not remove ${pc.name}.`);
        }
    };

    // A row's sentence, and its right side: only what the sentence has not
    // already said.
    const rowSentence = (pc, key) => {
        if (key === 'quiet') return <>Last seen <b>{daysText(pc.last_seen)} ago</b></>;
        if (key === 'lost') {
            return pc.last_seen
                ? <>Last seen <b>{shortDate(pc.last_seen)}</b> · {daysText(pc.last_seen)} ago</>
                : 'Never connected';
        }
        return problemSentence(pc);
    };
    const rowSide = (pc, key) => {
        const version = pc.client_version ? `v${pc.client_version}` : '';
        if (key === 'uninstalled' || key === 'stuck') {
            return pc.last_used ? `last used ${agoText(pc.last_used)}` : `seen ${agoText(pc.last_seen) || 'never'}`;
        }
        if (key === 'missing') {
            if (!pc.last_used) return 'not used in 90 days';
            // "used Xd ago" only when it differs from the silence the row states.
            return toMs(pc.last_seen) - toMs(pc.last_used) > 86400000 ? `used ${agoText(pc.last_used)}` : version;
        }
        return version;
    };

    const renderRow = (pc, key) => (
        <div
            key={pc.id}
            {...rowProps(pc)}
            ref={isLinked(pc) ? highlightRef : undefined}
            className={`cafe-pcs__issue${pc.id === openId ? ' is-selected' : ''}`}
        >
            <span className="cafe-pcs__issue-name" title={pc.name}>{pc.name}</span>
            <span className="cafe-pcs__issue-why">
                {rowSentence(pc, key)}
                {pc.reinstall_count >= REINSTALL_TAG_FROM && <span className="cafe-pcs__tag">reinstalled {pc.reinstall_count}×</span>}
            </span>
            <span className="cafe-pcs__issue-side" title={exactText(pc.last_used || pc.last_seen)}>{rowSide(pc, key)}</span>
            {key === 'lost' && canEdit
                ? (
                    <button type="button" className="cafe-pcs__rm" onClick={(e) => { e.stopPropagation(); removeListed(pc); }}>
                        Remove
                    </button>
                )
                : <ChevronRight size={16} className="cafe-pcs__issue-chev" />}
        </div>
    );

    // A PC with nothing wrong as a small tile: its name, how fresh, its version.
    const renderTile = (pc) => (
        <div
            key={pc.id}
            {...rowProps(pc)}
            ref={isLinked(pc) ? highlightRef : undefined}
            className={`cafe-pcs__tile${pc.state === 'inuse' ? ' is-inuse' : ''}${pc.id === openId ? ' is-selected' : ''}`}
            title={`${pc.name} · last seen ${exactText(pc.last_seen) || 'never'}`}
        >
            <b>{pc.name}</b>
            <span>
                <i className={`cafe-pcs__dot ${pc.online ? 'g' : ageDot(pc.last_seen)}`} />
                {pc.state === 'inuse' ? <span className="cafe-pcs__v-ok">In use</span>
                    : pc.state === 'idle' || pc.online ? 'On, idle'
                        : agoText(pc.last_seen) || 'never'}
                {pc.client_version && !pc.console && <span className="cafe-pcs__muted"> · v{pc.client_version}</span>}
            </span>
        </div>
    );

    const renderSection = (key) => {
        const sec = SECTIONS[key];
        const all = groups[key];
        if (all.length === 0) return null;
        const list = shown[key];
        const folded = key === 'lost' && group === 'all';
        const open = list.length > 0;
        const Icon = sec.icon;
        const head = (
            <>
                <span className="cafe-pcs__sec-icon"><Icon size={16} /></span>
                <h3>{sec.title}</h3>
                <span className="cafe-pcs__sec-n">{all.length}</span>
                <span className="cafe-pcs__sec-help" title={sec.help} aria-label={sec.help}><CircleHelp size={14} /></span>
            </>
        );
        return (
            <section key={key} className={`cafe-pcs__sec${GREY_SECTIONS.has(key) ? ' is-grey' : ''}`} style={{ '--c': sec.color }}>
                {folded ? (
                    <button type="button" className="cafe-pcs__sec-head is-toggle" aria-expanded={open} onClick={() => setLostOpen(!open)}>
                        {head}
                        <span className="cafe-pcs__sec-act">{open ? 'Hide' : 'Show'} <ChevronDown size={14} className={open ? 'is-open' : undefined} /></span>
                    </button>
                ) : <div className="cafe-pcs__sec-head">{head}</div>}
                {open && (TILE_SECTIONS.has(key)
                    ? <div className="cafe-pcs__tiles">{list.map(renderTile)}</div>
                    : <div className="cafe-pcs__issues">{list.map((pc) => renderRow(pc, key))}</div>)}
                {!folded && list.length < all.length && (
                    <button type="button" className="cafe-pcs__showall" onClick={() => setGroup(key)}>
                        Show all {all.length} <ArrowRight size={14} />
                    </button>
                )}
            </section>
        );
    };

    // Opens the create-café form with this owner prefilled: email and name
    // locked (onboarding matches the owner by email, so a changed email would
    // be a different account), phone editable (it becomes the new café's own
    // contact number).
    const addCafeForOwner = () => navigate('/cafes/new', {
        state: {
            prefillOwner: {
                name: cafe.owner_name || '',
                email: cafe.owner_email,
                phone: cafe.owner_phone || '',
                fromCafeId: cafe.id,
                fromCafeName: cafe.name,
            },
        },
    });

    // The ⋯ menu: what used to be the row of buttons under the name.
    const moreItems = cafe && canEdit && !isClosed ? [
        { label: 'Edit café', icon: Edit, onClick: () => navigate(`/cafes/${id}/edit`) },
        ...(cafe.owner_email ? [{ label: 'Add café for this owner', icon: Plus, onClick: addCafeForOwner }] : []),
        ...(cafe.status === 'active' ? [{ label: 'Archive café…', icon: Archive, danger: true, onClick: () => setArchiving(true) }] : []),
    ] : [];

    return (
        <div className="cafe-pcs">
            <Link to="/cafes" className="back-link"><ArrowLeft size={16} /><span>Cafés</span></Link>

            <div className="cafe-pcs__head">
                <div className="cafe-pcs__title">
                    <h1>{info.name}</h1>
                    {/* Jumps the whole page to another of this owner's cafés —
                        beside the name rather than on a row of its own. */}
                    {ownerCafes.length > 1 && (
                        <OwnerCafesPicker
                            cafes={ownerCafes}
                            currentId={cafe.id}
                            onPick={(cafeId) => navigate(`/cafes/${cafeId}`)}
                        />
                    )}
                    {status && <span className={`badge badge--${status.color}`}>{status.label}</span>}
                    {fee && (
                        <span
                            className={`badge badge--${fee.color}${fee.soon ? ' cafe-pcs__trial-soon' : ''}`}
                            title="When this café starts paying the platform fee — set on Billing & Revenue"
                        >
                            {fee.label}
                        </span>
                    )}
                    {tickets && tickets.length > 0 && (
                        <Link
                            to={tickets.length === 1 ? `/support?ticket=${tickets[0].id}` : '/support'}
                            className="badge badge--danger cafe-pcs__tickets"
                        >
                            <Ticket size={12} /> {tickets.length} open ticket{tickets.length === 1 ? '' : 's'}
                        </Link>
                    )}
                </div>
                {/* Every button in one place: the two used most, and the rest —
                    Archive above all, rare and destructive — behind ⋯. */}
                <div className="cafe-pcs__head-actions">
                    {canStats && (
                        <button type="button" className="btn btn--secondary" onClick={() => setShowStats(true)}>
                            <BarChart3 size={16} /> Statistics
                        </button>
                    )}
                    <button type="button" className="btn btn--primary" onClick={() => navigate(`/cafes/${id}/remote`)}>
                        <Monitor size={16} /> Remote access
                    </button>
                    <MoreMenu items={moreItems} />
                </div>
            </div>
            {archiving && cafe && (
                <ArchiveModal cafe={cafe} onClose={() => setArchiving(false)} onDone={loadCafe} />
            )}
            {isClosed && (
                <CafeLifecycle cafe={cafe} canEdit={canEdit} isSuperAdmin={isSuperAdmin()} onChanged={loadCafe} />
            )}

            {/* History + Contact as one line; "More" opens both in full. */}
            <div className="cafe-pcs__infoline">
                <span className="cafe-pcs__infoline-item">
                    <MapPin size={14} className="cafe-pcs__pin" />
                    {info.location || <span className="cafe-pcs__blank">No location saved</span>}
                </span>
                {info.owner && <span className="cafe-pcs__infoline-item is-strong">{info.owner}</span>}
                {info.phone && (
                    <span className="cafe-pcs__infoline-item">
                        <a href={`tel:${info.phone.replace(/[^\d+]/g, '')}`}>{info.phone}</a>
                        <CopyButton text={info.phone} label="Copy phone" />
                    </span>
                )}
                {historySummary && <span className="cafe-pcs__infoline-item">{historySummary}</span>}
                <span className="cafe-pcs__infoline-tools">
                    {contactAll && <CopyButton text={contactAll} label="Copy café name, owner, phone, email and location">Copy all</CopyButton>}
                    <button type="button" className="cafe-pcs__more-info" aria-expanded={infoOpen} onClick={toggleInfo}>
                        {infoOpen ? 'Less' : 'More'} <ChevronDown size={14} className={infoOpen ? 'is-open' : undefined} />
                    </button>
                </span>
            </div>
            {infoOpen && (
                <div className="cafe-pcs__info">
                    <section className="cafe-pcs__box">
                        <h3 className="cafe-pcs__box-title">History</h3>
                        <div className="cafe-pcs__item">
                            <span className="cafe-pcs__key">Joined NOLAG</span>
                            <span className="cafe-pcs__val"><ApproxDate iso={info.created_at} approx={info.created_at_approx} /></span>
                        </div>
                        <div className="cafe-pcs__item">
                            <span className="cafe-pcs__key">First PC</span>
                            <span className="cafe-pcs__val">
                                {installs
                                    ? <ApproxDate iso={installs.first.registered_at} approx={installs.first.registered_at_approx} />
                                    : <span className="cafe-pcs__blank">{data ? 'No PC installed yet' : '—'}</span>}
                            </span>
                        </div>
                        <div className="cafe-pcs__item">
                            <span className="cafe-pcs__key">Latest PC</span>
                            <span className="cafe-pcs__val">
                                {installs
                                    ? <><ApproxDate iso={installs.latest.registered_at} approx={installs.latest.registered_at_approx} /> <span className="cafe-pcs__muted">· {installs.latest.name}</span></>
                                    : <span className="cafe-pcs__blank">—</span>}
                            </span>
                        </div>
                        {/* Each revenue figure opens the Statistics pop-up behind it. */}
                        {cafe && (
                            <>
                                <div className="cafe-pcs__item">
                                    <span className="cafe-pcs__key">Revenue this month</span>
                                    <span className="cafe-pcs__val">
                                        {canStats ? (
                                            <button type="button" className="cafe-pcs__link" onClick={() => setShowStats(true)} title="Open Statistics">
                                                {formatCurrency(cafe.stats?.revenue_mtd)} <BarChart3 size={13} />
                                            </button>
                                        ) : formatCurrency(cafe.stats?.revenue_mtd)}
                                    </span>
                                </div>
                                <div className="cafe-pcs__item">
                                    <span className="cafe-pcs__key">Revenue all-time</span>
                                    <span className="cafe-pcs__val">
                                        {canStats ? (
                                            <button type="button" className="cafe-pcs__link" onClick={() => setShowStats(true)} title="Open Statistics">
                                                {formatCurrency(cafe.stats?.revenue_all_time)} <BarChart3 size={13} />
                                            </button>
                                        ) : formatCurrency(cafe.stats?.revenue_all_time)}
                                    </span>
                                </div>
                            </>
                        )}
                    </section>
                    <section className="cafe-pcs__box">
                        <h3 className="cafe-pcs__box-title">Contact</h3>
                        <div className="cafe-pcs__item">
                            <span className="cafe-pcs__key"><User size={14} /> Owner</span>
                            <span className="cafe-pcs__val">{info.owner || <span className="cafe-pcs__blank">Not saved</span>}</span>
                            {info.owner && <CopyButton text={info.owner} label="Copy owner" />}
                        </div>
                        <div className="cafe-pcs__item">
                            <span className="cafe-pcs__key"><Phone size={14} /> Phone</span>
                            <span className="cafe-pcs__val">
                                {info.phone
                                    ? <a href={`tel:${info.phone.replace(/[^\d+]/g, '')}`}>{info.phone}</a>
                                    : <span className="cafe-pcs__blank">Not saved</span>}
                            </span>
                            {info.phone && <CopyButton text={info.phone} label="Copy phone" />}
                        </div>
                        <div className="cafe-pcs__item">
                            <span className="cafe-pcs__key"><Mail size={14} /> Email</span>
                            <span className="cafe-pcs__val">
                                {info.email
                                    ? <a href={`mailto:${info.email}`}>{info.email}</a>
                                    : <span className="cafe-pcs__blank">Not saved</span>}
                            </span>
                            {info.email && <CopyButton text={info.email} label="Copy email" />}
                        </div>
                        <div className="cafe-pcs__item">
                            <span className="cafe-pcs__key"><MapPin size={14} /> Location</span>
                            <span className="cafe-pcs__val">{info.location || <span className="cafe-pcs__blank">Not saved</span>}</span>
                            {info.location && <CopyButton text={info.location} label="Copy location" />}
                        </div>
                    </section>
                </div>
            )}

            {data ? (
            <>
            <div className="cafe-pcs__viewbar">
                <div className="cafe-pcs__switch" role="tablist" aria-label="How to show the PCs">
                    <button type="button" role="tab" aria-selected={view === 'overview'} className={view === 'overview' ? 'is-on' : undefined} onClick={() => setView('overview')}>
                        Overview
                    </button>
                    <button type="button" role="tab" aria-selected={view === 'details'} className={view === 'details' ? 'is-on' : undefined} onClick={() => setView('details')}>
                        All details
                    </button>
                </div>
                <div className="cafe-pcs__list-tools">
                    <span className="cafe-pcs__legend"><i className="cafe-pcs__dot g" />today or yesterday <i className="cafe-pcs__dot a" />2–7 days <i className="cafe-pcs__dot r" />over a week</span>
                    <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        onClick={() => exportCsv(info.name, view === 'details' ? filter : 'all', view === 'details' ? rows : order)}
                        title="Download the PCs shown below, in this order"
                    >
                        <Download size={14} /> Export CSV
                    </button>
                </div>
            </div>
            {error && <p className="modal-error">{error}</p>}

            {/* The café's health in one box: how many PCs are running, the bar,
                the chips that pick a group, how fresh that is, and — when the
                whole café has gone silent — that, here rather than in a banner. */}
            <section className="cafe-pcs__health">
                <div className="cafe-pcs__health-top">
                    <span className="cafe-pcs__health-big">
                        {health.working} <span>of {health.total} PC{health.total === 1 ? '' : 's'} working</span>
                    </span>
                    <span className="cafe-pcs__health-use">
                        {percentText(counts.used24, counts.all)} used in the last 24h · {counts.inuse} in use now
                    </span>
                </div>
                {health.total > 0 && (
                    // Each piece explains itself on hover and, like its chip, shows
                    // only those PCs when clicked (again: everything).
                    <div className="cafe-pcs__barwrap" ref={barRef}>
                        <div className={`cafe-pcs__bar${view === 'overview' && group !== 'all' ? ' has-pick' : ''}`} role="group" aria-label="PCs by state">
                            {BAR_ORDER.map((k) => groups[k].length > 0 && (
                                <button
                                    key={k}
                                    type="button"
                                    className={view === 'overview' && group === k ? 'is-on' : undefined}
                                    style={{ flex: groups[k].length, '--c': SECTIONS[k].color }}
                                    aria-label={`${groups[k].length} ${SECTIONS[k].title.toLowerCase()}: show them`}
                                    onMouseEnter={(e) => showBarTip(k, e.currentTarget)}
                                    onFocus={(e) => showBarTip(k, e.currentTarget)}
                                    onMouseLeave={() => setBarTip(null)}
                                    onBlur={() => setBarTip(null)}
                                    onClick={() => pickGroup(k)}
                                />
                            ))}
                        </div>
                        {barTip && (
                            <div className="cafe-pcs__bartip" role="tooltip" style={{ left: barTip.left, '--c': SECTIONS[barTip.key].color }}>
                                <span className="cafe-pcs__bartip-arrow" style={{ left: barTip.arrow - barTip.left + BAR_TIP_HALF }} />
                                <div className="cafe-pcs__bartip-head">
                                    <i />
                                    <b>{groups[barTip.key].length}</b> {SECTIONS[barTip.key].title.toLowerCase()}
                                    <span>{percentText(groups[barTip.key].length, health.total)} of PCs</span>
                                </div>
                                <p>{BAR_TIP[barTip.key]}</p>
                                <p className="cafe-pcs__bartip-hint">
                                    {view === 'overview' && group === barTip.key ? 'Click to show all PCs again' : 'Click to show only these'}
                                </p>
                            </div>
                        )}
                    </div>
                )}
                {/* Why the headline is more than the green: PCs on an old version
                    work too. */}
                {groups.stuck.length > 0 && (
                    <p className="cafe-pcs__explain">
                        {health.working} working = <b className="is-fine">{groups.fine.length} working normally</b>
                        {' + '}<b className="is-old">{groups.stuck.length} on an old version</b>
                    </p>
                )}
                <div className="cafe-pcs__chips">
                    {chips.map(renderChip)}
                </div>
                {info.quiet_since && (
                    <p className="cafe-pcs__quiet" title={`Last PC connected ${exactText(info.quiet_since) || 'unknown'}`}>
                        <AlertTriangle size={15} /> No PC has connected for {daysText(info.quiet_since)}. The café may be closed or offline.
                    </p>
                )}
                {/* The grey (uncounted) chips share the last line rather than
                    pushing a row of their own under the coloured ones. */}
                <div className="cafe-pcs__foot">
                <p className="cafe-pcs__checked">
                    {view === 'overview' && group !== 'all'
                        ? <>Showing only {SECTIONS[group].chip} · <button type="button" className="cafe-pcs__back" onClick={() => setGroup('all')}>← Back to all PCs</button> · </>
                        : <>Click a number to see only those PCs · </>}
                    {data.checked_at
                        ? <>missing PCs and versions are checked every 30 minutes · last check {agoText(data.checked_at)}</>
                        : 'missing PCs and versions have not been checked recently, so they may not show.'}
                </p>
                {greyChips.length > 0 && <span className="cafe-pcs__chips-grey">{greyChips.map(renderChip)}</span>}
                </div>
            </section>

            <div className={`cafe-pcs__body${selected && view === 'overview' ? ' has-panel' : ''}`}>
            <div className="cafe-pcs__main">
            {view === 'overview' ? (
                <>
                {pcs.length === 0 && (
                    <div className="empty-state"><Monitor size={32} /><p>No PCs registered for this café yet.</p></div>
                )}
                {group === 'all' && health.total > 0 && health.working === health.total && !groups.stuck.length && (
                    <p className="cafe-pcs__allclear">
                        <Check size={16} /> All {health.total} PC{health.total === 1 ? ' is' : 's are'} working
                    </p>
                )}
                {shownKeys.map(renderSection)}
                {group !== 'all' && groups[group].length === 0 && (
                    <p className="cafe-pcs__none">
                        No PCs here now. <button type="button" className="cafe-pcs__back" onClick={() => setGroup('all')}>← Back to all PCs</button>
                    </p>
                )}
                </>
            ) : (
            <>
            <div className="cafe-pcs__cards">
                {CARDS.map((card) => {
                    const n = counts[card.key];
                    const on = filter === card.key;
                    return (
                        <button
                            key={card.key}
                            type="button"
                            className={`cafe-pcs__card${on ? ' cafe-pcs__card--active' : ''}`}
                            aria-pressed={on}
                            onClick={() => setFilter(on && card.key !== 'all' ? 'all' : card.key)}
                        >
                            <span
                                className={`cafe-pcs__value${card.alarm && n > 0 ? ' cafe-pcs__value--alarm' : ''}${card.warn && n > 0 ? ' cafe-pcs__value--warn' : ''}`}
                            >
                                {n}
                                {card.percent && <span className="cafe-pcs__pct">{percentText(n, counts.all)}</span>}
                            </span>
                            <span className="cafe-pcs__label">{card.label}</span>
                            {card.key === 'all' && groups.lost.length > 0 && (
                                <span className="cafe-pcs__sub" title="Not seen in the last 90 days">+{groups.lost.length} lost</span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="cafe-pcs__list-head">
                <h2>{filter === 'all' ? 'All PCs' : `${activeCard.label} (${rows.length})`}</h2>
                <span className="cafe-pcs__list-note">
                    {sort.key === ATTENTION.key
                        ? 'Problems first · click a heading to sort'
                        : <button type="button" className="cafe-pcs__textbtn" onClick={() => setSort(ATTENTION)}>Problems first again</button>}
                </span>
            </div>

            <div className="card table-card cafe-pcs__table">
                <table className="data-table">
                    <thead>
                        <tr>
                            {COLUMNS.map((col) => (
                                <th key={col.key} aria-sort={sort.key === col.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                    <button type="button" className="cafe-pcs__sort" onClick={() => onSort(col)}>
                                        {col.label}
                                        <span className="cafe-pcs__arrow">{sort.key === col.key ? (sort.dir === 'asc' ? '↑' : '↓') : ''}</span>
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={COLUMNS.length}>
                                    <div className="empty-state">
                                        <Monitor size={32} />
                                        <p>{pcs.length === 0 ? 'No PCs registered for this café yet.' : 'No PCs here.'}</p>
                                    </div>
                                </td>
                            </tr>
                        ) : rows.map((pc, i) => {
                            const st = STATUS[pc.state];
                            const problem = problemOf(pc);
                            // A line under the last problem, while they are on top.
                            const lastProblem = sort.key === ATTENTION.key && problem && rows[i + 1] && !problemOf(rows[i + 1]);
                            const n = pc.reinstall_count || 0;
                            return (
                                <tr
                                    key={pc.id}
                                    {...rowProps(pc)}
                                    ref={isLinked(pc) ? highlightRef : undefined}
                                    className={[
                                        'cafe-pcs__row',
                                        problem ? `is-${problem}` : '',
                                        lastProblem ? 'is-last-problem' : '',
                                        isApart(pc) ? 'cafe-pcs__row--old' : '',
                                        isLinked(pc) ? 'cafe-pcs__row--highlight' : '',
                                        pc.id === openId ? 'is-selected' : '',
                                    ].join(' ').trim()}
                                >
                                    <td className="cafe-pcs__name">{pc.name}</td>
                                    <td><span className={`badge badge--${st.badge}`}>{st.label}</span></td>
                                    <td title={exactText(pc.last_seen)}>
                                        {pc.last_seen
                                            ? <><i className={`cafe-pcs__dot ${ageDot(pc.last_seen)}`} /><span className="cafe-pcs__strong">{agoText(pc.last_seen)}</span></>
                                            : <span className="cafe-pcs__blank">Never</span>}
                                    </td>
                                    <td title={pc.last_used ? exactText(pc.last_used) : 'Not used in the last 90 days'}>
                                        {pc.state === 'inuse' ? 'Now' : (agoText(pc.last_used) || <span className="cafe-pcs__blank">—</span>)}
                                    </td>
                                    <td title={pc.expected_version ? `Should be on ${pc.expected_version}` : undefined}><VersionLine pc={pc} /></td>
                                    <td
                                        className={pc.registered_at_approx ? 'cafe-pcs__muted' : undefined}
                                        title={pc.registered_at_approx ? APPROX_HINT : exactText(pc.registered_at)}
                                    >
                                        {dateText(pc.registered_at, pc.registered_at_approx) || <span className="cafe-pcs__blank">Not recorded</span>}
                                    </td>
                                    <td title={exactText(pc.last_reinstalled_at)}>
                                        {pc.last_reinstalled_at
                                            ? <>{dateText(pc.last_reinstalled_at)} <span className="cafe-pcs__muted">({n}×)</span></>
                                            : <span className="cafe-pcs__blank">—</span>}
                                    </td>
                                    <td title={exactText(pc.uninstalled_at)}>
                                        {pc.uninstalled_at
                                            ? <span className="cafe-pcs__alarm">{dateText(pc.uninstalled_at)}</span>
                                            : <span className="cafe-pcs__blank">—</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            </>
            )}
            </div>
            {selected && (
                <PcPanel
                    key={selected.id}
                    pc={selected}
                    cafeId={id}
                    canEdit={canEdit}
                    floating={view === 'details' || narrow}
                    onClose={() => setSelectedId(null)}
                    onRemoved={() => { setSelectedId(null); reload(); }}
                />
            )}
            </div>
            </>
            ) : (
                <div className="cafe-pcs__pcs-error">
                    <Monitor size={32} />
                    <p>{error || 'Could not read this café’s PCs.'}</p>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={reload} disabled={isLoading}>
                        {isLoading ? 'Loading…' : 'Try again'}
                    </button>
                </div>
            )}

            {showStats && canStats && (
                <CafeInsightsModal cafeId={cafe.id} cafeName={cafe.name} onClose={() => setShowStats(false)} />
            )}

            <style>{`
        .cafe-pcs {
          /* noLag accent, scoped to this page — same as the remote page. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);
          /* Overview's group colours, most serious first; grey = not counted. */
          --pc-uninstalled: #f04b4b; --pc-missing: #fb7a3c; --pc-stuck: #f5c024; --pc-grey: #8b93a7;
          max-width: 1200px;
        }
        /* Name, badges and the owner's cafés on the left; Remote access, the
           one orange button, at the top right. */
        .cafe-pcs__head {
          display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-4);
          flex-wrap: wrap; margin-bottom: var(--space-4);
        }
        .cafe-pcs__tickets { display: inline-flex; align-items: center; gap: 4px; text-decoration: none; }
        .cafe-pcs__tickets:hover { filter: brightness(1.15); }
        /* Owner's cafés, beside the name. */
        .owner-picker { position: relative; display: inline-flex; }
        .owner-picker__btn {
          display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px;
          border: 1px solid rgba(255, 255, 255, 0.18); border-radius: var(--radius-md); background: rgba(255, 255, 255, 0.04);
          font: inherit; font-size: var(--text-xs); color: var(--text-secondary); cursor: pointer; white-space: nowrap;
        }
        .owner-picker__btn:hover, .owner-picker__btn[aria-expanded="true"] { color: var(--text-primary); border-color: rgba(255, 255, 255, 0.32); }
        .owner-picker__menu {
          position: absolute; top: calc(100% + 6px); left: 0; z-index: 20; min-width: 260px; max-width: min(420px, 90vw);
          max-height: 320px; overflow-y: auto; padding: 4px; display: flex; flex-direction: column;
          background: #14161d; border: 1px solid rgba(255, 255, 255, 0.16); border-radius: var(--radius-md);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55);
        }
        .owner-picker__item {
          display: flex; align-items: center; justify-content: space-between; gap: var(--space-3);
          padding: 8px 10px; border: none; border-radius: var(--radius-md); background: none; text-align: left;
          font: inherit; font-size: var(--text-sm); color: var(--text-secondary); cursor: pointer;
        }
        .owner-picker__item:hover { background: rgba(255, 255, 255, 0.07); color: var(--text-primary); }
        .owner-picker__item.is-current { color: var(--text-primary); font-weight: 600; }
        .owner-picker__item svg { color: var(--accent-primary); flex-shrink: 0; }
        .cafe-pcs__link {
          display: inline-flex; align-items: center; gap: 6px; padding: 0; border: none; background: none;
          font: inherit; color: inherit; cursor: pointer;
        }
        .cafe-pcs__link svg { color: var(--text-tertiary); }
        .cafe-pcs__link:hover, .cafe-pcs__link:hover svg { color: var(--accent-primary); }
        .cafe-pcs__pcs-error {
          display: flex; flex-direction: column; align-items: center; gap: var(--space-3);
          padding: var(--space-8) var(--space-4); color: var(--text-tertiary); text-align: center;
        }
        .loading-state { display: flex; align-items: center; justify-content: center; min-height: 50vh; }
        .back-link {
          display: inline-flex; align-items: center; gap: var(--space-2);
          color: var(--text-secondary); font-size: var(--text-sm); margin-bottom: var(--space-6);
          transition: color var(--duration-fast) var(--ease-out);
        }
        .back-link:hover { color: var(--accent-primary); }

        .cafe-pcs__title { display: flex; align-items: center; gap: var(--space-4); flex-wrap: wrap; margin-bottom: var(--space-2); }
        .cafe-pcs__title h1 { font-size: var(--text-2xl); font-weight: 600; letter-spacing: -0.02em; margin: 0; }
        /* History + Contact: the café page's info-card recipe, smaller. */
        .cafe-pcs__info {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: var(--space-4); margin: var(--space-4) 0 var(--space-6);
        }
        .cafe-pcs__box {
          align-self: start; min-width: 0;
          padding: var(--space-2) var(--space-4);
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.05) 45%, rgba(255, 255, 255, 0.03) 100%);
          border: 1px solid rgba(255, 255, 255, 0.14);
          border-radius: 16px;
        }
        .cafe-pcs__box-title {
          flex-shrink: 0; font-size: var(--text-xs); font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;
          color: var(--text-tertiary);
        }
        .cafe-pcs__chev { flex-shrink: 0; margin-left: auto; color: var(--text-tertiary); transition: transform var(--duration-fast) var(--ease-out); }
        .cafe-pcs__item {
          display: flex; align-items: center; gap: var(--space-3); min-height: 32px;
          font-size: var(--text-sm); border-top: 1px solid var(--divider);
        }
        .cafe-pcs__key {
          display: inline-flex; align-items: center; gap: 6px; flex: 0 0 140px; color: var(--text-tertiary);
        }
        .cafe-pcs__val { flex: 1 1 auto; min-width: 0; color: var(--text-primary); font-weight: 500; overflow-wrap: anywhere; }
        .cafe-pcs__val a { color: inherit; text-decoration: none; }
        .cafe-pcs__val a:hover { color: var(--accent-primary); text-decoration: underline; }
        .cafe-pcs__approx {
          display: inline-block; margin-left: var(--space-2); padding: 0 6px; border-radius: var(--radius-full);
          font-size: var(--text-xs); font-weight: 400; color: var(--text-tertiary); background: rgba(255, 255, 255, 0.08);
          cursor: help;
        }
        .cafe-pcs__copy {
          display: inline-flex; align-items: center; gap: 4px; flex-shrink: 0;
          padding: 4px 6px; border: none; border-radius: var(--radius-md); background: none;
          font: inherit; font-size: var(--text-xs); color: var(--text-tertiary); cursor: pointer;
        }
        .cafe-pcs__copy:hover { color: var(--text-primary); background: rgba(255, 255, 255, 0.06); }
        .cafe-pcs__copy--ok { color: var(--status-success); }

        /* The seven filters as slim chips: count and label on one line. */
        .cafe-pcs__cards {
          display: flex; flex-wrap: wrap; gap: var(--space-2); margin-bottom: var(--space-6);
        }
        .cafe-pcs__card {
          display: inline-flex; align-items: baseline; gap: var(--space-2);
          padding: 6px 12px; text-align: left; cursor: pointer; color: var(--text-primary);
          background: rgba(255, 255, 255, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.16);
          border-radius: var(--radius-md);
          transition: all var(--duration-fast) var(--ease-out);
        }
        .cafe-pcs__card:hover { border-color: rgba(255, 255, 255, 0.32); }
        .cafe-pcs__card--active { border-color: var(--accent-primary); background: var(--accent-primary-subtle); }
        .cafe-pcs__value { display: inline-flex; align-items: baseline; gap: 6px; font-size: var(--text-lg); font-weight: 600; }
        .cafe-pcs__value--alarm { color: var(--status-danger); }
        .cafe-pcs__value--warn { color: var(--status-warning); }
        .cafe-pcs__pct { font-size: var(--text-xs); font-weight: 500; color: var(--text-secondary); }
        .cafe-pcs__label { font-size: var(--text-sm); color: var(--text-secondary); white-space: nowrap; }
        .cafe-pcs__sub { font-size: var(--text-xs); color: var(--text-quaternary, var(--text-tertiary)); white-space: nowrap; }

        .cafe-pcs__list-head {
          display: flex; align-items: baseline; justify-content: space-between; gap: var(--space-4);
          flex-wrap: wrap; margin-bottom: var(--space-3);
        }
        .cafe-pcs__list-head h2 { font-size: var(--text-lg); font-weight: 600; margin: 0; }
        .cafe-pcs__list-tools { display: flex; align-items: center; gap: var(--space-4); flex-wrap: wrap; }
        .cafe-pcs__list-tools > span { font-size: var(--text-xs); color: var(--text-tertiary); }

        .table-card { padding: 0; overflow: hidden; }
        .cafe-pcs__table { overflow-x: auto; }
        /* The table must never widen the page. The layout's columns are flex
           items that grow to fit their content, so a wide table pushed the whole
           page sideways. width 0 + min-width 100% keeps the table out of that
           sizing: it fills the space the page has and, only where a screen is
           truly too narrow, scrolls inside its own box. */
        .cafe-pcs__table { width: 0; min-width: 100%; }
        .cafe-pcs__table .data-table { min-width: 720px; }
        .cafe-pcs__table th, .cafe-pcs__table td { padding: var(--space-3) var(--space-4); }
        .cafe-pcs__table th { white-space: nowrap; }
        .cafe-pcs__table td { font-size: var(--text-sm); color: var(--text-secondary); white-space: nowrap; user-select: text; }
        /* Long PC names wrap rather than widen the table. */
        .cafe-pcs__table td.cafe-pcs__name { white-space: normal; overflow-wrap: anywhere; min-width: 80px; }
        .cafe-pcs__sort {
          display: inline-flex; align-items: center; gap: 4px; padding: 0; background: none; border: none;
          font: inherit; color: inherit; letter-spacing: inherit; text-transform: inherit; cursor: pointer;
        }
        .cafe-pcs__sort:hover { color: var(--text-primary); }
        .cafe-pcs__arrow { display: inline-block; min-width: 10px; color: var(--accent-primary); }
        .cafe-pcs .cafe-pcs__name { font-weight: 600; color: var(--text-primary); }
        .cafe-pcs__muted { color: var(--text-tertiary); }
        .cafe-pcs__blank { color: var(--text-tertiary); }
        .cafe-pcs__alarm { color: var(--status-danger); }
        .cafe-pcs__row--old td { opacity: 0.45; }
        .cafe-pcs__row--highlight td { background: rgba(var(--accent-primary-rgb), 0.12); }

        /* ── 2026-10-16 layout ── */
        /* The name and its badges wrap within their own column, so a long name or
           many badges never push the buttons onto a row of their own. */
        .cafe-pcs__head { flex-wrap: nowrap; align-items: flex-start; }
        .cafe-pcs__head .cafe-pcs__title { flex: 1 1 0; min-width: 0; }
        .cafe-pcs__head .cafe-pcs__title h1 { overflow-wrap: anywhere; }
        .cafe-pcs__head-actions { display: flex; align-items: center; gap: var(--space-2); flex-shrink: 0; }
        .cafe-pcs__trial-soon { box-shadow: inset 0 0 0 1px var(--status-warning); }
        .cafe-pcs__more { position: relative; display: inline-flex; }
        .cafe-pcs__more-btn { padding-left: 10px; padding-right: 10px; }
        .cafe-pcs__menu {
          position: absolute; top: calc(100% + 6px); right: 0; z-index: 30; min-width: 220px; padding: 4px;
          display: flex; flex-direction: column; background: #14161d; border: 1px solid rgba(255, 255, 255, 0.16);
          border-radius: var(--radius-md); box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55);
        }
        .cafe-pcs__menu-item {
          display: flex; align-items: center; gap: var(--space-2); padding: 9px 10px; border: none; border-radius: var(--radius-md);
          background: none; text-align: left; font: inherit; font-size: var(--text-sm); color: var(--text-primary); cursor: pointer;
        }
        .cafe-pcs__menu-item svg { color: var(--text-tertiary); }
        .cafe-pcs__menu-item:hover { background: rgba(255, 255, 255, 0.07); }
        .cafe-pcs__menu-item.is-danger, .cafe-pcs__menu-item.is-danger svg { color: var(--status-danger); }
        .cafe-pcs__menu-item.is-danger { border-top: 1px solid var(--divider); border-radius: 0 0 var(--radius-md) var(--radius-md); }

        /* History + Contact as one line. */
        .cafe-pcs__infoline {
          display: flex; align-items: center; flex-wrap: wrap; gap: 6px var(--space-4); margin: 0 0 var(--space-5);
          font-size: var(--text-sm); color: var(--text-secondary);
        }
        .cafe-pcs__infoline-item { display: inline-flex; align-items: center; gap: 4px; }
        .cafe-pcs__infoline-item.is-strong { color: var(--text-primary); font-weight: 500; }
        .cafe-pcs__infoline-item a { color: inherit; text-decoration: none; }
        .cafe-pcs__infoline-item a:hover { color: var(--accent-primary); }
        .cafe-pcs__pin { color: var(--accent-primary); }
        .cafe-pcs__infoline-tools { display: inline-flex; align-items: center; gap: var(--space-2); margin-left: auto; }
        .cafe-pcs__more-info {
          display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: var(--radius-md); background: rgba(255, 255, 255, 0.04); font: inherit; font-size: var(--text-xs);
          font-weight: 500; color: var(--text-primary); cursor: pointer;
        }
        .cafe-pcs__more-info:hover { border-color: rgba(255, 255, 255, 0.32); }
        .cafe-pcs__more-info svg, .cafe-pcs__tech-toggle svg, .cafe-pcs__chev { transition: transform var(--duration-fast) var(--ease-out); }
        .cafe-pcs__more-info svg.is-open, .cafe-pcs__chev.is-open { transform: rotate(180deg); }
        .cafe-pcs__info { margin-top: calc(-1 * var(--space-2)); }
        .cafe-pcs__box { padding: var(--space-3) var(--space-4); }
        .cafe-pcs__box-title { margin: 0 0 var(--space-1); }

        /* Overview / All details, and the tools beside it. */
        .cafe-pcs__viewbar { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); flex-wrap: wrap; margin-bottom: var(--space-4); }
        .cafe-pcs__switch { display: inline-flex; padding: 3px; border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 10px; background: rgba(255, 255, 255, 0.04); }
        .cafe-pcs__switch button {
          padding: 6px 14px; border: none; border-radius: 7px; background: none; font: inherit; font-size: var(--text-sm);
          font-weight: 500; color: var(--text-secondary); cursor: pointer;
        }
        .cafe-pcs__switch button:hover { color: var(--text-primary); }
        .cafe-pcs__switch button.is-on { background: var(--accent-primary-subtle); color: var(--accent-primary); }
        .cafe-pcs__legend { display: inline-flex; align-items: center; gap: 6px; }
        .cafe-pcs__legend .cafe-pcs__dot:not(:first-child) { margin-left: var(--space-2); }
        .cafe-pcs__dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; margin-right: 6px; vertical-align: 1px; flex-shrink: 0; }
        .cafe-pcs__dot.g { background: var(--status-success); }
        .cafe-pcs__dot.a { background: var(--status-warning); }
        .cafe-pcs__dot.r { background: var(--status-danger); }
        .cafe-pcs__textbtn { padding: 0; border: none; background: none; font: inherit; color: var(--text-secondary); cursor: pointer; text-decoration: underline; text-underline-offset: 2px; }
        .cafe-pcs__textbtn:hover { color: var(--accent-primary); }

        /* The health box. */
        .cafe-pcs__health {
          padding: var(--space-4) var(--space-5); margin-bottom: var(--space-6);
          background: rgba(255, 255, 255, 0.035); border: 1px solid rgba(255, 255, 255, 0.10); border-radius: 14px;
        }
        .cafe-pcs__health-top { display: flex; align-items: baseline; justify-content: space-between; gap: var(--space-3); flex-wrap: wrap; }
        .cafe-pcs__health-big { font-size: 28px; font-weight: 700; color: var(--text-primary); }
        .cafe-pcs__health-big span { font-size: var(--text-base); font-weight: 500; color: var(--text-secondary); }
        .cafe-pcs__health-use { font-size: var(--text-sm); color: var(--text-secondary); }
        .cafe-pcs__barwrap { position: relative; margin: var(--space-3) 0 var(--space-2); }
        .cafe-pcs__bar { display: flex; gap: 2px; height: 12px; }
        .cafe-pcs__bar button {
          min-width: 6px; height: 100%; padding: 0; border: none; background: var(--c); cursor: pointer;
          transition: filter var(--duration-fast) var(--ease-out), opacity var(--duration-fast) var(--ease-out), box-shadow var(--duration-fast) var(--ease-out);
        }
        .cafe-pcs__bar button:first-child { border-radius: 6px 0 0 6px; }
        .cafe-pcs__bar button:last-child { border-radius: 0 6px 6px 0; }
        .cafe-pcs__bar button:only-child { border-radius: 6px; }
        /* Hovering one piece dims the rest; the piece itself glows in its colour. */
        .cafe-pcs__bar:hover button:not(:hover), .cafe-pcs__bar.has-pick button:not(.is-on) { opacity: 0.35; }
        .cafe-pcs__bar button:hover, .cafe-pcs__bar button:focus-visible {
          opacity: 1; filter: brightness(1.15); outline: none;
          box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.6), 0 0 14px color-mix(in srgb, var(--c) 70%, transparent);
        }
        /* The hover card, under the piece; 270px = BAR_TIP_HALF * 2. */
        .cafe-pcs__bartip {
          position: absolute; top: calc(100% + 12px); z-index: 20; width: 270px; margin-left: -135px;
          padding: 10px 12px; border-radius: 10px; pointer-events: none;
          background: #15161e; border: 1px solid color-mix(in srgb, var(--c) 55%, transparent);
          box-shadow: 0 14px 36px rgba(0, 0, 0, 0.6); animation: cafe-pcs-tip 120ms var(--ease-out);
        }
        @keyframes cafe-pcs-tip { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
        .cafe-pcs__bartip-arrow {
          position: absolute; top: -6px; width: 10px; height: 10px; margin-left: -5px; transform: rotate(45deg);
          background: #15161e; border-top: 1px solid color-mix(in srgb, var(--c) 55%, transparent); border-left: 1px solid color-mix(in srgb, var(--c) 55%, transparent);
        }
        .cafe-pcs__bartip-head { display: flex; align-items: center; gap: 6px; font-size: var(--text-sm); color: var(--text-primary); }
        .cafe-pcs__bartip-head i { width: 9px; height: 9px; border-radius: 3px; background: var(--c); flex-shrink: 0; }
        .cafe-pcs__bartip-head b { color: var(--c); font-size: var(--text-base); }
        .cafe-pcs__bartip-head span { margin-left: auto; font-size: var(--text-xs); color: var(--text-tertiary); }
        .cafe-pcs__bartip p { margin: 4px 0 0; font-size: var(--text-xs); line-height: 1.4; color: var(--text-secondary); }
        .cafe-pcs__bartip p.cafe-pcs__bartip-hint { margin-top: 6px; color: var(--accent-primary); font-weight: 500; }
        .cafe-pcs__v-stuck { color: var(--pc-stuck); font-weight: 600; }
        .cafe-pcs__v-ok { color: var(--status-success); }
        .cafe-pcs__quiet {
          display: flex; align-items: center; gap: var(--space-2); margin: var(--space-3) 0 0; padding: var(--space-2) var(--space-3);
          border-radius: var(--radius-md); background: var(--status-danger-subtle); color: var(--status-danger); font-size: var(--text-sm); font-weight: 500;
        }
        .cafe-pcs__checked { margin: var(--space-3) 0 0; font-size: var(--text-xs); color: var(--text-tertiary); }
        .cafe-pcs__foot { display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2) var(--space-4); margin-top: var(--space-3); }
        .cafe-pcs__foot .cafe-pcs__checked { margin: 0; flex: 1 1 320px; }

        /* The list, and the panel beside it (Overview) or over it (All details). */
        .cafe-pcs__body { display: grid; grid-template-columns: minmax(0, 1fr); gap: var(--space-5); align-items: start; }
        .cafe-pcs__body.has-panel { grid-template-columns: minmax(0, 1fr) 360px; }
        .cafe-pcs__main { min-width: 0; }
        .cafe-pcs__allclear {
          display: flex; align-items: center; gap: var(--space-2); margin: 0 0 var(--space-5); padding: var(--space-3) var(--space-4);
          border-radius: 12px; background: rgba(34, 197, 94, 0.1); color: var(--status-success); font-weight: 500;
        }
        /* The chips under the bar. */
        .cafe-pcs__explain { margin: 0; font-size: var(--text-sm); color: var(--text-secondary); }
        .cafe-pcs__explain b { font-weight: 600; }
        .cafe-pcs__explain .is-fine { color: var(--status-success); }
        .cafe-pcs__explain .is-old { color: var(--pc-stuck); }
        .cafe-pcs__chips { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2); margin-top: var(--space-3); }
        .cafe-pcs__chips-grey { display: inline-flex; flex-wrap: wrap; gap: var(--space-2); margin-left: auto; }
        .cafe-pcs__chip {
          --c: var(--text-secondary);
          display: inline-flex; align-items: center; gap: 7px; padding: 6px 12px; white-space: nowrap; cursor: pointer;
          border: 1px solid rgba(255, 255, 255, 0.18); border-radius: 9px; background: rgba(255, 255, 255, 0.06);
          font: inherit; font-size: var(--text-sm); color: var(--text-secondary);
          transition: border-color var(--duration-fast) var(--ease-out), background var(--duration-fast) var(--ease-out);
        }
        .cafe-pcs__chip svg { color: var(--c); }
        .cafe-pcs__chip b { font-size: var(--text-base); color: var(--text-primary); }
        .cafe-pcs__chip:hover:not(:disabled) { border-color: rgba(255, 255, 255, 0.34); }
        .cafe-pcs__chip.is-flag { background: color-mix(in srgb, var(--c) 11%, transparent); border-color: color-mix(in srgb, var(--c) 45%, transparent); }
        .cafe-pcs__chip.is-flag b { color: var(--c); }
        .cafe-pcs__chip.is-on { color: var(--text-primary); border-color: var(--c); background: color-mix(in srgb, var(--c) 22%, transparent); box-shadow: 0 0 0 1px var(--c); }
        .cafe-pcs__chip.is-small { padding: 5px 10px; font-size: var(--text-xs); }
        .cafe-pcs__chip.is-small b { font-size: var(--text-sm); }
        .cafe-pcs__chip:disabled { opacity: 0.45; cursor: default; }
        .cafe-pcs__back { padding: 0; border: none; background: none; font: inherit; color: var(--accent-primary); cursor: pointer; }
        .cafe-pcs__back:hover { text-decoration: underline; }
        .cafe-pcs__none { color: var(--text-secondary); font-size: var(--text-sm); }

        /* Overview's groups: each a box with its colour down the left edge. */
        .cafe-pcs__sec {
          --c: var(--pc-grey);
          margin-bottom: var(--space-4); overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.10); border-radius: 14px;
          background: rgba(20, 20, 28, 0.85); box-shadow: inset 4px 0 0 var(--c);
        }
        .cafe-pcs__sec-head {
          display: flex; align-items: center; gap: 10px; width: 100%; padding: 10px var(--space-4) 10px 18px;
          background: linear-gradient(90deg, color-mix(in srgb, var(--c) 16%, transparent), color-mix(in srgb, var(--c) 2%, transparent));
        }
        .cafe-pcs__sec.is-grey .cafe-pcs__sec-head { background: rgba(255, 255, 255, 0.03); }
        .cafe-pcs__sec-head.is-toggle { border: none; font: inherit; color: inherit; text-align: left; cursor: pointer; }
        .cafe-pcs__sec-head h3 { margin: 0; font-size: var(--text-base); font-weight: 600; }
        .cafe-pcs__sec-icon {
          display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 9px;
          background: color-mix(in srgb, var(--c) 18%, transparent); color: var(--c);
        }
        .cafe-pcs__sec-n { padding: 1px 9px; border-radius: 999px; font-size: var(--text-xs); font-weight: 700; background: color-mix(in srgb, var(--c) 18%, transparent); color: var(--c); }
        .cafe-pcs__sec.is-grey .cafe-pcs__sec-n { color: var(--text-secondary); background: rgba(255, 255, 255, 0.1); }
        .cafe-pcs__sec.is-grey .cafe-pcs__sec-icon { color: var(--text-secondary); background: rgba(255, 255, 255, 0.08); }
        .cafe-pcs__sec-help { display: inline-flex; color: var(--text-tertiary); cursor: help; }
        .cafe-pcs__sec-act { display: inline-flex; align-items: center; gap: 4px; margin-left: auto; font-size: var(--text-xs); color: var(--text-secondary); }
        .cafe-pcs__sec-act svg.is-open { transform: rotate(180deg); }
        .cafe-pcs__showall {
          display: flex; align-items: center; gap: 6px; width: 100%; padding: 9px 18px; border: none; border-top: 1px solid rgba(255, 255, 255, 0.06);
          background: none; font: inherit; font-size: var(--text-sm); font-weight: 500; color: var(--accent-primary); cursor: pointer; text-align: left;
        }
        .cafe-pcs__showall:hover { background: rgba(255, 255, 255, 0.03); }
        .cafe-pcs__issues { border-top: 1px solid rgba(255, 255, 255, 0.10); }
        .cafe-pcs__issue {
          display: grid; grid-template-columns: minmax(120px, 200px) minmax(0, 1fr) auto auto; align-items: center; gap: var(--space-3);
          min-height: 46px; padding: 6px var(--space-4) 6px 18px; border-top: 1px solid rgba(255, 255, 255, 0.06);
          background: rgba(255, 255, 255, 0.03); cursor: pointer; user-select: text;
        }
        .cafe-pcs__issue:first-child { border-top: none; }
        .cafe-pcs__issue:nth-child(even) { background: rgba(255, 255, 255, 0.055); }
        .cafe-pcs__issue:hover { background: rgba(255, 255, 255, 0.09); }
        .cafe-pcs__issue.is-selected { background: color-mix(in srgb, var(--c) 16%, transparent); box-shadow: inset 3px 0 0 var(--c); }
        .cafe-pcs__issue:focus-visible, .cafe-pcs__tile:focus-visible, .cafe-pcs__row:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: -2px; }
        .cafe-pcs__issue-name { font-weight: 600; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .cafe-pcs__issue-why { min-width: 0; font-size: var(--text-sm); color: var(--text-secondary); }
        .cafe-pcs__issue-why b { color: var(--text-primary); font-weight: 600; }
        .cafe-pcs__hl { color: var(--c); font-weight: 600; }
        .cafe-pcs__issue-side { font-size: var(--text-xs); color: var(--text-tertiary); white-space: nowrap; text-align: right; }
        .cafe-pcs__issue-chev { color: var(--text-tertiary); }
        .cafe-pcs__rm {
          padding: 3px 9px; border: 1px solid rgba(240, 75, 75, 0.4); border-radius: 7px; background: none;
          font: inherit; font-size: var(--text-xs); color: var(--pc-uninstalled); cursor: pointer;
        }
        .cafe-pcs__rm:hover { background: rgba(240, 75, 75, 0.12); }
        .cafe-pcs__tag { display: inline-block; margin-left: var(--space-2); padding: 0 7px; border-radius: 4px; background: rgba(255, 255, 255, 0.08); color: var(--text-tertiary); font-size: var(--text-xs); }
        .cafe-pcs__tiles {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px;
          padding: 14px var(--space-4) var(--space-4) 18px; border-top: 1px solid rgba(255, 255, 255, 0.10);
        }
        .cafe-pcs__tile {
          display: flex; flex-direction: column; gap: 3px; min-width: 0; padding: 10px 12px; cursor: pointer; user-select: text;
          border: 1px solid rgba(255, 255, 255, 0.15); border-left: 3px solid var(--c); border-radius: 11px; background: rgba(255, 255, 255, 0.075);
        }
        .cafe-pcs__tile:hover { border-color: rgba(255, 255, 255, 0.3); border-left-color: var(--c); }
        .cafe-pcs__tile b { font-size: var(--text-sm); color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .cafe-pcs__tile > span { font-size: var(--text-xs); color: var(--text-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .cafe-pcs__tile.is-inuse { border-color: rgba(34, 197, 94, 0.55); border-left-color: var(--c); background: rgba(34, 197, 94, 0.10); }
        .cafe-pcs__tile.is-selected { border-color: var(--accent-primary); background: rgba(var(--accent-primary-rgb), 0.12); }
        .cafe-pcs__list-note { font-size: var(--text-xs); color: var(--text-tertiary); }

        /* All details rows. */
        .cafe-pcs__row { cursor: pointer; }
        .cafe-pcs__row:hover td { background: rgba(255, 255, 255, 0.03); }
        .cafe-pcs__row.is-stuck td:first-child { box-shadow: inset 3px 0 0 var(--pc-stuck); }
        .cafe-pcs__row.is-missing td:first-child { box-shadow: inset 3px 0 0 var(--pc-missing); }
        .cafe-pcs__row.is-uninstalled td:first-child { box-shadow: inset 3px 0 0 var(--pc-uninstalled); }
        .cafe-pcs__row.is-last-problem td { border-bottom: 1px solid rgba(255, 255, 255, 0.22); }
        .cafe-pcs__row.is-selected td { background: rgba(var(--accent-primary-rgb), 0.09); }
        .cafe-pcs__table td { padding-top: var(--space-2); padding-bottom: var(--space-2); }
        .cafe-pcs__strong { color: var(--text-primary); }

        /* The details panel: buttons at the top, one compact list, so it fits a
           laptop screen; the scrollbar is only a fallback for a short window. */
        .cafe-pcs__panel {
          --pc-uninstalled: #f04b4b; --pc-missing: #fb7a3c; --pc-stuck: #f5c024; --pc-grey: #8b93a7;
          position: sticky; top: var(--space-4); padding: var(--space-4); max-height: calc(100vh - 2 * var(--space-4)); overflow-y: auto;
          background: #101015; border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 14px;
        }
        .cafe-pcs__panel.is-floating {
          position: fixed; top: 0; right: 0; bottom: 0; z-index: 150; width: min(380px, 100vw); max-height: none;
          --accent-primary: #FE7B00; --accent-primary-rgb: 254, 123, 0;
          border-radius: 14px 0 0 14px; box-shadow: -16px 0 40px rgba(0, 0, 0, 0.55);
        }
        .cafe-pcs__panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-3); }
        .cafe-pcs__panel-title { min-width: 0; }
        .cafe-pcs__panel-head h2 { margin: 0 0 6px; font-size: var(--text-lg); font-weight: 600; overflow-wrap: anywhere; }
        .cafe-pcs__panel-close { padding: 4px; border: none; background: none; color: var(--text-tertiary); cursor: pointer; }
        .cafe-pcs__panel-close:hover { color: var(--text-primary); }
        .cafe-pcs__pill {
          display: inline-block; padding: 3px 10px; border-radius: 6px; font-size: var(--text-xs); font-weight: 600;
          background: color-mix(in srgb, var(--c) 16%, transparent); color: var(--c);
        }
        .cafe-pcs__panel-actions { display: flex; flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-3); }
        .cafe-pcs__panel-actions .btn--primary { flex: 1; justify-content: center; }
        .cafe-pcs__danger-btn { color: var(--pc-uninstalled); border-color: rgba(240, 75, 75, 0.4); }
        .cafe-pcs__advice {
          margin: var(--space-3) 0 0; padding: 9px 11px; border-radius: 9px; font-size: var(--text-sm); line-height: 1.45; color: var(--text-secondary);
          background: color-mix(in srgb, var(--c) 9%, transparent); border: 1px solid color-mix(in srgb, var(--c) 30%, transparent);
        }
        .cafe-pcs__kv { display: grid; grid-template-columns: 120px minmax(0, 1fr); gap: 0 var(--space-3); margin: var(--space-2) 0 0; font-size: var(--text-sm); }
        .cafe-pcs__kv dt, .cafe-pcs__kv dd { padding: 6px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.06); }
        .cafe-pcs__kv dt { color: var(--text-tertiary); }
        .cafe-pcs__kv dd { margin: 0; color: var(--text-primary); overflow-wrap: anywhere; user-select: text; }
        .cafe-pcs__tech-toggle {
          display: flex; align-items: center; gap: 4px; width: 100%; margin: var(--space-2) 0 0; padding: var(--space-2) 0 0;
          border: none; background: none; font: inherit; font-size: var(--text-sm); color: var(--text-secondary); cursor: pointer;
        }
        .cafe-pcs__tech-toggle svg.is-open { transform: rotate(90deg); }

        @media (max-width: 900px) {
          .cafe-pcs__body.has-panel { grid-template-columns: minmax(0, 1fr); }
          .cafe-pcs__panel, .cafe-pcs__panel.is-floating {
            position: fixed; inset: 0; z-index: 150; width: auto; max-height: none; border-radius: 0;
          }
          .cafe-pcs__issue { grid-template-columns: minmax(0, 1fr) auto auto; }
          .cafe-pcs__issue-why { grid-column: 1 / -1; grid-row: 2; }
          .cafe-pcs__chips-grey, .cafe-pcs__infoline-tools { margin-left: 0; }
          .cafe-pcs__head { flex-wrap: wrap; }
          .cafe-pcs__head-actions { width: 100%; }
        }
      `}</style>
        </div>
    );
}
